import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import ReactDOM from "react-dom"; // Step 1: Import ReactDOM for portals
import './Sheet.css';
import '../Container.css';
import { Container } from "../Container";
import { calculatePosition } from "./calculatePosition";
import { Space } from "../Space";

interface SheetProps {
  anchor?: React.RefObject<HTMLElement>;
  active: boolean;
  children: any;
  onDismiss?: any;
  size?: 'compact' | 'medium' | 'expanded';
  height?: 'hug' | 'fill'
}

const Sheet: React.FC<SheetProps> = ({
  anchor = null,
  active,
  children,
  onDismiss,
  size = 'compact',
  height = 'fill'
}) => {

  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(active);
  const [sheetStyles, setSheetStyles] = useState<{
    top: number;
    left: number;
    maxHeight: number;
    transformOrigin: string;
    translateY: string;
  }>({
    top: 0,
    left: 0,
    maxHeight: 0,
    transformOrigin: "top center",
    translateY: "0",
  });

  const sheetRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {

    if (active) {

      if (anchor?.current) {
        anchor.current.style.opacity = '0.6';
      }

      setIsVisible(true);

      setTimeout(() => {

        calculatePosition(anchor, size, sheetRef, setSheetStyles);

        requestAnimationFrame(() => {
          setIsAnimating(true);
        });

      }, 100);

    } else {

      if (anchor?.current) { anchor.current.style.opacity = '1' }
      setIsAnimating(false);
      setTimeout(() => {
        setIsVisible(false); // Hide after animation completes
      }, 100); // Match this timeout with the transition duration
    }

  }, [active]);



  useEffect(() => {
    if (!isVisible) return;

    const sheetElement = sheetRef.current;
    if (!sheetElement) return;

    const focusableElements = sheetElement.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleFocus = () => {
      if (firstElement) {
        firstElement.focus();
      } else {
        sheetElement.focus({ preventScroll: true });
      }
    };

    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === "Tab") {
        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement?.focus();
        } else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement?.focus();
        }
      }
      if (event.key === "Escape") {
        onDismiss?.();
      }
    };

    const handleTransitionEnd = () => {
      requestAnimationFrame(handleFocus);
    };

    // Attach keydown and transitionend listeners
    sheetElement.addEventListener("keydown", handleKeyPress);
    sheetElement.addEventListener("transitionend", handleTransitionEnd);

    return () => {
      // Cleanup event listeners
      sheetElement.removeEventListener("keydown", handleKeyPress);
      sheetElement.removeEventListener("transitionend", handleTransitionEnd);
    };
  }, [isVisible, onDismiss]);


  const containerStyle: any = {
    "compact": {
      width: '240px',
      height: height === 'fill' ? '350px' : 'auto',
      maxHeight: '350px',
      margin: 'var(--ts-space-v-section) 0px'
    },
    "medium": {
      width: '640px',
      maxWidth: '100%',
      height: height === 'fill' ? 'calc(100% - var(--ts-space-v-section) * 2' : 'auto',
      maxHeight: '640px',
    },
    "expanded": {
      width: 'calc(100% - var(--ts-space-h-section) * 2',
      maxWidth: 'var(--ts-size-max-page-bleed)',
      height: height === 'fill' ? 'calc(100% - var(--ts-space-v-section) * 2)' : 'auto'
    }
  }


  const sheetContent = isVisible ? (
    <>
      {size !== 'expanded' && anchor !== null ? (
        <>
          <div className="overlay" style={{
            opacity: isAnimating ? size !== 'compact' ? 1 : 0 : 0,
            transition: 'opacity .1s ease',
            pointerEvents: isAnimating ? 'auto' : 'none',
          }} onClick={() => onDismiss()} />

          <div
            className="sheet-bounds"
            ref={sheetRef}
            tabIndex={1}
            style={{
              position: "absolute", // Keep this as absolute
              top: `${sheetStyles.top}px`,
              left: `${sheetStyles.left}px`,
              width: size === 'compact' ? '250px' : size === 'medium' ? '340px' : size === 'expanded' ? '100%' : '250px',
              maxHeight: `calc(100vh - 32px)`,
              transformOrigin: sheetStyles.transformOrigin,
              transform: `translateY(${sheetStyles.translateY})`,
              scale: `${isAnimating ? '1' : size !== 'compact' ? '.05' : '.8'}`,
              opacity: isAnimating ? 1 : 0,
              transition: `scale ${size !== 'compact' ? '.2s' : '.05s'} cubic-bezier( 0.19, 1, 0.22, 1 ), opacity ${size !== 'compact' ? '.2s' : '.1s'} ease`,
            }}
          >
            <div
              className={`container sheet-container v-stack ${size}`}>
              {children}
            </div>
          </div>
        </>
      ) : (
        <div
          ref={sheetRef}
          className="sheet-unanchored-bounds unanchored v-stack height-fill width-fill middle center">
          <div
            className="overlay"
            style={{
              opacity: isAnimating ? 1 : 0,
              zIndex: '0',
              transition: 'opacity .2s ease-out',
              pointerEvents: isAnimating ? 'auto' : 'none',
            }}
            onClick={() => onDismiss()} />

          {/* Moves it to bottom */}
          <Space.Grow showOn="compact" />
          <div
            style={{
              transform: size !== 'compact' ? `translateY(${isAnimating ? '0%' : '150%'})` : `scale(${isAnimating ? '1' : '.8'})`,
              opacity: size !== 'compact' ? '1' : isAnimating ? 1 : 0,
              transition: 'transform' + size !== 'compact' ? '.8s' : '.25s',
              transitionTimingFunction: 'cubic-bezier( 0.19, 1, 0.22, 1 )',
              overflowY: 'auto',
              width: containerStyle[size].width,
              height: containerStyle[size].height,
              maxHeight: containerStyle[size].maxHeight,
              maxWidth: containerStyle[size].maxWidth,
              margin: containerStyle[size].margin
            }}
            className={`container container-default sheet-container v-stack ${size}`}
          >
            {children}
          </div>
        </div>
      )}
    </>
  ) : null;

  // Step 2: Return the portal
  return sheetContent ? ReactDOM.createPortal(sheetContent, document.body) : null;
};

export { Sheet, SheetProps };

